/**
 * Text fitter
 *
 * @copyright 2018 Linguistica 360
 */

export class ImageTextFitter {
    constructor(ctx, width, height) {
        this.ctx = ctx;
        this.width = width;
        this.height = height;

        this.nFont = '{}px Arial';
        this.bFont = 'bold {}px Arial';

        this.spacing = 1.2;

        this.min = 4;
        this.max = 32;

        this.x = 0;
        this.y = 0;
    }

    fitText(text) {
        const segments = this._breakdown(text);

        for (let size = this.max; size >= this.min; --size) {
            const forceFit = size === this.min;

            const result = this._fitTextSegments(segments, size, forceFit);
            if (result.length) return {
                size,
                segments: this._addLineWidth(result),
                height: this.y,
            };
        }
    }

    _fitTextSegments(segments, size, forceFit = false) {
        this.x = 0;
        this.y = size;

        let results = [];

        for (const segment of segments) {
            const result = this._fitTextSegment(segment, size, forceFit);

            if (result === false) return false;
            if (!forceFit && this.y >= this.height) return false;

            results = results.concat(result);
        }

        return results;
    }

    _fitTextSegment(segment, size, forceFit = false) {
        const font = segment.bold ? this.bFont : this.nFont;
        if (segment.newline) this._newline(size);

        const subsegments = [];

        let text = segment.text;
        while (text) {
            const width = Math.max(this.width - this.x, 0);
            let result = this._fitSubSegment(text, size, font, width);

            if (!result && this.x !== 0) {
                if (text !== segment.text && text[0] === ' ') {
                    text = text.substr(1);
                }

                const width = this.width;
                result = this._fitSubSegment(text, size, font, width, forceFit);

                if (result) {
                    this._newline(size);
                    if (!forceFit && this.y >= this.height) return false;
                }
            }

            if (!result) return false;

            result.subsegment.x = this.x;
            result.subsegment.y = this.y;
            result.subsegment.bold = segment.bold;
            subsegments.push(result.subsegment);

            this.x += result.subsegment.width;

            text = result.rest;
        }

        return subsegments;
    }

    _fitSubSegment(text, size, font, width, forceFit = false) {
        let result = false;
        const indices = [];

        for (let i = 1; i < text.length; ++i) {
            if ([' ', '-'].includes(text[i])) indices.push(i);
        }
        indices.push(text.length);

        for (const offset of indices) {
            this.ctx.font = font.replace('{}', size);

            const partial = text.substr(0, offset);
            const mwidth = this.ctx.measureText(partial).width;

            if (mwidth < width || (forceFit && offset === text.length)) {
                const rest = text.substr(offset);
                result = {
                    rest,
                    subsegment: { text: partial, width: mwidth },
                };
            }
            else break;
        }

        return result;
    }

    _newline(size) {
        this.x = 0;
        this.y += this.spacing * size;
    }

    _breakdown(text) {
        let bold = false;

        const segments = [];
        text.replace(/\\\*/g, '{_ASTERISK_}').split('*').forEach((part) => {
            part.split('\n').forEach((line, i) => {
                if (line.trim() || i > 0) {
                    segments.push({
                        bold,
                        text: line.replace(/{_ASTERISK_}/g, '*'),
                        newline: i > 0,
                    });
                }
            });

            bold = !bold;
        });

        return segments;
    }

    _addLineWidth(segments) {
        const lines = {};

        segments.forEach((segment) => {
            if (!lines[segment.y]) lines[segment.y] = 0;
            lines[segment.y] += segment.width;
        });

        segments.forEach((segment) => { segment.linewidth = lines[segment.y]; });

        return segments;
    }
}
